import { useEffect, useRef, useState } from 'react';
import { enviarFormulario } from './api';
import { rotulo } from './formato';
import { useCaptura, type PedidoDeFoto } from './Camera';

/**
 * Fotografias do recebimento (item 7 do briefing).
 *
 * **A foto é de uma AMOSTRA, não de um monte.** A Testesolo definiu um
 * roteiro que se repete para cada amostra: embalagem externa,
 * acondicionamento, etiqueta de identificação e amostra aberta. O técnico
 * não escolhe o que a foto é depois de tirá-la — toca no item do roteiro e
 * a câmera abre já sabendo. Um campo "tipo" preenchido depois é um campo
 * que fica em `outro`.
 *
 * **Duas delas são obrigatórias** (migração `0012`): embalagem externa e
 * etiqueta de identificação. São as duas que respondem às duas perguntas
 * que um laudo contestado faz — como veio e com que identificação veio.
 * Quem recusa é o banco, na conclusão do recebimento; aqui a tela avisa
 * antes para o técnico não descobrir isso com a caixa já guardada.
 *
 * **Nada é reduzido aqui.** O original é a evidência (restrição 25); quem
 * encolhe é o gerador do documento, para o PDF caber num e-mail. Reduzir
 * no celular gravaria como prova uma imagem que ninguém fotografou.
 */

/**
 * O roteiro, na ordem em que se fotografa. Casa com a migração `0010`.
 *
 * Só os códigos: o texto do botão sai de `rotulo()`, o MESMO tradutor que
 * o relatório usa. Um rótulo curto aqui e um longo no PDF fariam o técnico
 * tocar em "Etiqueta" e o cliente ler "Etiqueta de identificação" — duas
 * palavras para a mesma foto, que é como a conferência começa a não bater.
 * O teste 10.9 compara as duas listas.
 */
export const ROTEIRO = [
  'embalagem_externa', 'acondicionamento', 'etiqueta_original', 'amostra_aberta',
] as const;

/** As que o banco exige para concluir o recebimento (migração `0012`). */
export const MINIMO = ['embalagem_externa', 'etiqueta_original'] as const;

/**
 * O que se fotografa do LOTE (migração `0015`). Todas opcionais.
 *
 * As três primeiras respondem à pergunta que ninguém consegue responder
 * depois: quem trouxe e em que estado o conjunto desceu do caminhão.
 */
export const TIPOS_DO_LOTE = [
  'conjunto', 'veiculo', 'recibo_entrega', 'outro',
] as const;

/** Limites do servidor (`arquivo.service.ts`), repetidos para avisar antes. */
export const LIMITE_BYTES = 12 * 1024 * 1024;
export const LIMITE_POR_ENVIO = 10;

export interface FotoPendente {
  chave: string;
  arquivo: File;
  previa: string;
  tipo: string;
  /** Índice na lista de amostras. `null` = foto do lote. */
  amostraIndice: number | null;
  observacao: string;
}

export interface AmostraParaFoto {
  /** O que o técnico lê no cartão: código ou "Amostra 3 — SP-01 · 1,00–1,45". */
  rotulo: string;
}

let sequencia = 0;

function preparar(
  lista: File[], tipo: string, amostraIndice: number | null,
): { fotos: FotoPendente[]; recusadas: string[] } {
  const fotos: FotoPendente[] = [];
  const recusadas: string[] = [];
  for (const arquivo of lista) {
    if (arquivo.size > LIMITE_BYTES) {
      recusadas.push(
        `${arquivo.name} tem ${(arquivo.size / 1048576).toFixed(1)} MB — o limite é 12 MB.`);
      continue;
    }
    fotos.push({
      chave: `f${++sequencia}`,
      arquivo,
      previa: URL.createObjectURL(arquivo),
      tipo,
      amostraIndice,
      observacao: '',
    });
  }
  return { fotos, recusadas };
}

export function descartarPrevias(fotos: FotoPendente[]) {
  fotos.forEach((f) => URL.revokeObjectURL(f.previa));
}

/** Quantos itens do mínimo esta amostra já tem. */
export function minimoCumprido(fotos: FotoPendente[], amostraIndice: number): boolean {
  return MINIMO.every((t) =>
    fotos.some((f) => f.amostraIndice === amostraIndice && f.tipo === t));
}

// ── Peças compartilhadas ─────────────────────────────────────────────

interface CartaoProps {
  foto: FotoPendente;
  comTipo: boolean;
  aberto: boolean;
  aoAbrir(): void;
  aoAlterar(m: Partial<FotoPendente>): void;
  aoRemover(): void;
}

function CartaoFoto({ foto, comTipo, aberto, aoAbrir, aoAlterar, aoRemover }: CartaoProps) {
  return (
    <div className="foto">
      <img src={foto.previa} alt={`Fotografia: ${rotulo(foto.tipo)}`} />
      <div className="leg">
        {comTipo && <b>{rotulo(foto.tipo)}</b>}
        {foto.observacao && <span>{foto.observacao}</span>}
        <div className="linha" style={{ marginTop: '.35rem', gap: '.3rem' }}>
          <button
            type="button" className="botao secundario"
            style={{ minHeight: 34, fontSize: '.78rem' }}
            aria-expanded={aberto}
            onClick={aoAbrir}
          >
            {aberto ? 'Fechar' : 'Descrever'}
          </button>
          <button
            type="button" className="botao discreto"
            style={{ minHeight: 34, fontSize: '.78rem' }}
            onClick={aoRemover}
            aria-label={`Remover a foto ${rotulo(foto.tipo)}`}
          >
            Remover
          </button>
        </div>
        {aberto && (
          <input
            style={{ marginTop: '.4rem' }}
            value={foto.observacao}
            maxLength={1000}
            autoFocus
            placeholder="ex.: tampa solta na extremidade"
            aria-label="Observação da fotografia"
            onChange={(e) => aoAlterar({ observacao: e.target.value })}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Estado compartilhado por quem mostra fotos pendentes.
 *
 * A captura mora aqui porque o `<input type="file">` tem que ser um só na
 * página, e porque a decisão entre câmera nativa e webcam é a mesma em
 * todos os lugares que pedem foto.
 */
function usePainelDeFotos(fotos: FotoPendente[], aoMudar: (f: FotoPendente[]) => void) {
  const [recusadas, setRecusadas] = useState<string[]>([]);
  const [detalhe, setDetalhe] = useState<string | null>(null);
  const atual = useRef(fotos);
  atual.current = fotos;

  const captura = useCaptura((arquivos, pedido) => {
    const r = preparar(arquivos, pedido.tipo, pedido.amostraIndice);
    setRecusadas(r.recusadas);
    if (r.fotos.length) aoMudar([...atual.current, ...r.fotos]);
  });

  const alterar = (chave: string, m: Partial<FotoPendente>) =>
    aoMudar(atual.current.map((f) => (f.chave === chave ? { ...f, ...m } : f)));

  const remover = (chave: string) => {
    const f = atual.current.find((x) => x.chave === chave);
    if (f) URL.revokeObjectURL(f.previa);
    aoMudar(atual.current.filter((x) => x.chave !== chave));
    if (detalhe === chave) setDetalhe(null);
  };

  const cartao = (f: FotoPendente, comTipo = true) => (
    <CartaoFoto
      key={f.chave}
      foto={f}
      comTipo={comTipo}
      aberto={detalhe === f.chave}
      aoAbrir={() => setDetalhe(detalhe === f.chave ? null : f.chave)}
      aoAlterar={(m) => alterar(f.chave, m)}
      aoRemover={() => remover(f.chave)}
    />
  );

  const avisoRecusadas = recusadas.length > 0 && (
    <div className="aviso erro" role="alert">
      <strong>Estas não entraram:</strong>
      <ul>{recusadas.map((r, i) => <li key={i}>{r}</li>)}</ul>
    </div>
  );

  return { captura, cartao, avisoRecusadas };
}

interface BotoesProps {
  tipos: readonly string[];
  fotos: FotoPendente[];
  amostraIndice: number | null;
  obrigatorios?: readonly string[];
  desabilitado?: boolean;
  pedirFoto(p: PedidoDeFoto): void;
  pedirArquivo(p: PedidoDeFoto): void;
}

/**
 * Um botão por item, e a marca do que já foi feito.
 *
 * O toque longo (ou o botão "arquivo" ao lado) existe porque nem toda foto
 * é tirada na hora: às vezes o cliente mandou a imagem por mensagem, e
 * obrigar a fotografar a tela do celular seria pior que aceitar o arquivo.
 */
function BotoesDeFoto({
  tipos, fotos, amostraIndice, obrigatorios = [], desabilitado,
  pedirFoto, pedirArquivo,
}: BotoesProps) {
  return (
    <div className="escolha-fotos">
      {tipos.map((t) => {
        const quantas = fotos.filter(
          (f) => f.amostraIndice === amostraIndice && f.tipo === t).length;
        const exigido = obrigatorios.includes(t);
        const pedido: PedidoDeFoto = { tipo: t, titulo: rotulo(t), amostraIndice };
        return (
          <div
            className={`botao-foto${quantas ? ' feito' : ''}${
              exigido && !quantas ? ' exigido' : ''}`}
            key={t}
          >
            <button
              type="button"
              disabled={desabilitado}
              aria-pressed={quantas > 0}
              data-tipo={t}
              onClick={() => pedirFoto(pedido)}
            >
              <span className="marca-foto" aria-hidden="true">{quantas ? '✓' : '📷'}</span>
              <span data-rotulo>{rotulo(t)}</span>
              {exigido && !quantas && <span className="exig">obrigatória</span>}
              {quantas > 1 && <span className="mono"> ({quantas})</span>}
            </button>
            <button
              type="button"
              className="anexo"
              disabled={desabilitado}
              title={`Escolher um arquivo para ${rotulo(t)}`}
              aria-label={`Escolher arquivo para ${rotulo(t)}`}
              onClick={() => pedirArquivo(pedido)}
            >
              📎
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ── As fotos de UMA amostra, no passo dela ───────────────────────────

interface FotosDaAmostraProps {
  fotos: FotoPendente[];
  aoMudar(f: FotoPendente[]): void;
  amostraIndice: number;
  desabilitado?: boolean;
}

/**
 * Usada dentro da tela de cada amostra (decisão `D-20`): as fotos são
 * pedidas na hora do cadastro, com a amostra na mão — não num passo
 * depois, quando a caixa já voltou para a prateleira.
 */
export function FotosDaAmostra({
  fotos, aoMudar, amostraIndice, desabilitado,
}: FotosDaAmostraProps) {
  const { captura, cartao, avisoRecusadas } = usePainelDeFotos(fotos, aoMudar);
  const minhas = fotos.filter((f) => f.amostraIndice === amostraIndice);
  const faltam = MINIMO.filter((t) => !minhas.some((f) => f.tipo === t));

  return (
    <div>
      {captura.entradas}
      {captura.sobreposicao}
      {avisoRecusadas}

      <BotoesDeFoto
        tipos={ROTEIRO}
        fotos={fotos}
        amostraIndice={amostraIndice}
        obrigatorios={MINIMO}
        desabilitado={desabilitado}
        pedirFoto={captura.pedirFoto}
        pedirArquivo={captura.pedirArquivo}
      />

      {faltam.length > 0 && (
        <p className="sub" style={{ marginTop: '.5rem' }}>
          Faltam {faltam.map((t) => rotulo(t).toLowerCase()).join(' e ')} — sem elas
          o recebimento não pode ser concluído.
        </p>
      )}

      {minhas.length > 0 && (
        <div className="grade-fotos" style={{ marginTop: '.7rem' }}>
          {minhas.map((f) => cartao(f))}
        </div>
      )}
    </div>
  );
}

// ── As fotos do lote ─────────────────────────────────────────────────

interface FotosDoLoteProps {
  fotos: FotoPendente[];
  aoMudar(f: FotoPendente[]): void;
  desabilitado?: boolean;
}

export function FotosDoLote({ fotos, aoMudar, desabilitado }: FotosDoLoteProps) {
  const { captura, cartao, avisoRecusadas } = usePainelDeFotos(fotos, aoMudar);
  const doLote = fotos.filter((f) => f.amostraIndice === null);

  return (
    <div>
      {captura.entradas}
      {captura.sobreposicao}
      {avisoRecusadas}

      <BotoesDeFoto
        tipos={TIPOS_DO_LOTE}
        fotos={fotos}
        amostraIndice={null}
        desabilitado={desabilitado}
        pedirFoto={captura.pedirFoto}
        pedirArquivo={captura.pedirArquivo}
      />

      {doLote.length > 0 && (
        <div className="grade-fotos" style={{ marginTop: '.7rem' }}>
          {doLote.map((f) => cartao(f))}
        </div>
      )}
    </div>
  );
}

// ── Roteiro completo, para acrescentar fotos a um lote já gravado ────

interface RoteiroProps {
  fotos: FotoPendente[];
  aoMudar(f: FotoPendente[]): void;
  amostras: AmostraParaFoto[];
  desabilitado?: boolean;
}

export function RoteiroFotos({ fotos, aoMudar, amostras, desabilitado }: RoteiroProps) {
  const { captura, cartao, avisoRecusadas } = usePainelDeFotos(fotos, aoMudar);
  const doLote = fotos.filter((f) => f.amostraIndice === null);

  return (
    <div>
      {captura.entradas}
      {captura.sobreposicao}
      {avisoRecusadas}

      {amostras.length === 0 && (
        <p className="vazio">Nenhuma amostra registrada ainda.</p>
      )}

      {amostras.map((a, i) => {
        const minhas = fotos.filter((f) => f.amostraIndice === i);
        const feitos = ROTEIRO.filter((t) => minhas.some((f) => f.tipo === t)).length;
        const ok = minimoCumprido(fotos, i);
        return (
          <div className="cartao" key={i}>
            <div className="linha-topo" style={{ marginBottom: '.5rem' }}>
              <h3 className="cresce">{a.rotulo}</h3>
              <span className={`selo ${ok ? 'aceite' : 'rejeicao'}`}>
                {feitos} de {ROTEIRO.length}
              </span>
            </div>

            <BotoesDeFoto
              tipos={ROTEIRO}
              fotos={fotos}
              amostraIndice={i}
              obrigatorios={MINIMO}
              desabilitado={desabilitado}
              pedirFoto={captura.pedirFoto}
              pedirArquivo={captura.pedirArquivo}
            />

            {minhas.length > 0 && (
              <div className="grade-fotos" style={{ marginTop: '.7rem' }}>
                {minhas.map((f) => cartao(f))}
              </div>
            )}
          </div>
        );
      })}

      <div className="cartao">
        <div className="linha-topo" style={{ marginBottom: '.5rem' }}>
          <h3 className="cresce">Do lote</h3>
        </div>
        <p className="sub" style={{ marginBottom: '.6rem' }}>
          O que não é de uma amostra só: as amostras em conjunto, o veículo ou a
          transportadora que trouxe, o recibo de entrega. Todas opcionais.
        </p>
        <BotoesDeFoto
          tipos={TIPOS_DO_LOTE}
          fotos={fotos}
          amostraIndice={null}
          desabilitado={desabilitado}
          pedirFoto={captura.pedirFoto}
          pedirArquivo={captura.pedirArquivo}
        />
        {doLote.length > 0 && (
          <div className="grade-fotos" style={{ marginTop: '.7rem' }}>
            {doLote.map((f) => cartao(f))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Envio ────────────────────────────────────────────────────────────

export interface ItemEvidencia {
  tipo: string;
  amostraId: string | null;
  observacao: string | null;
}

/**
 * Sobe as fotos em blocos de dez.
 *
 * Dez é o limite por requisição no servidor. Blocos maiores seriam
 * recusados; blocos de um seriam quarenta e oito idas ao servidor num lote
 * de doze amostras — exatamente o que a rota de evidências foi feita para
 * evitar num galpão com sinal ruim.
 *
 * As fotos vão na ordem do roteiro, amostra por amostra, porque `ordem` no
 * banco é atribuída na sequência da inserção e é ela que o documento usa
 * como desempate.
 */
export async function enviarFotos(
  loteId: string,
  fotos: FotoPendente[],
  idsDasAmostras: string[],
  aoProgredir?: (fracao: number) => void,
): Promise<void> {
  const blocos: FotoPendente[][] = [];
  for (let i = 0; i < fotos.length; i += LIMITE_POR_ENVIO) {
    blocos.push(fotos.slice(i, i + LIMITE_POR_ENVIO));
  }

  for (let b = 0; b < blocos.length; b++) {
    const bloco = blocos[b];
    const fd = new FormData();
    const itens: ItemEvidencia[] = bloco.map((f) => ({
      tipo: f.tipo,
      amostraId: f.amostraIndice === null ? null : idsDasAmostras[f.amostraIndice] ?? null,
      observacao: f.observacao.trim() || null,
    }));
    for (const f of bloco) fd.append('fotos', f.arquivo, f.arquivo.name);
    fd.append('itens', JSON.stringify(itens));

    await enviarFormulario(`/recebimento/${loteId}/evidencias`, fd, (fr) =>
      aoProgredir?.((b + fr) / blocos.length));
  }
}

/** Ordena as pendentes como o documento vai imprimi-las. */
export function ordenarParaEnvio(fotos: FotoPendente[]): FotoPendente[] {
  const posicao = (t: string) => {
    const i = ROTEIRO.indexOf(t as (typeof ROTEIRO)[number]);
    return i < 0 ? ROTEIRO.length : i;
  };
  return [...fotos].sort((a, b) =>
    (a.amostraIndice ?? 1e9) - (b.amostraIndice ?? 1e9)
    || posicao(a.tipo) - posicao(b.tipo));
}

/** Barra de progresso do envio, com valor legível por leitor de tela. */
export function Progresso({ fracao, texto }: { fracao: number; texto: string }) {
  const pct = Math.round(fracao * 100);
  return (
    <div
      role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}
      aria-label={texto}
      style={{
        height: 10, borderRadius: 999, overflow: 'hidden',
        background: 'var(--fundo-3)', border: '1px solid var(--borda)',
      }}
    >
      <div style={{ width: `${pct}%`, height: '100%', background: 'var(--oliva)' }} />
    </div>
  );
}

/** Limpa as prévias quando o componente que as criou sai da tela. */
export function useLimpezaDePrevias(fotos: FotoPendente[]) {
  const ref = useRef(fotos);
  ref.current = fotos;
  useEffect(() => () => descartarPrevias(ref.current), []);
}
